import Button from "@components/Button"
import PasswordInput from "@components/PasswordInput"
import FunnelHeader from "@components/quiz/FunnelHeader"
import FunnelLayout from "@components/quiz/FunnelLayout"
import StickyFooter from "@components/quiz/StickyFooter"
import Spinner from "@components/Spinner"
import StatusIcon from "@components/StatusIcon"
import {
  authEmailLinkFor,
  verifyAuthEmailLinkOnce,
} from "@helpers/authEmailLink"
import { supabase, updatePassword } from "@helpers/supabase"
import { validatePassword } from "@helpers/validations"
import { CheckIcon, ClockIcon } from "@heroicons/react/24/outline"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"

const SIGN_IN_PATH = "/account/sign-in"
const REDIRECT_DELAY_MS = 3000

type ResetPasswordState =
  | "verifying"
  | "form"
  | "linkExpired"
  | "passwordUpdated"

export default function ResetPasswordPage() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [password, setPassword] = useState<string>("")

  const { pathname } = useLocation()
  // NOTE: The reset email's link: a `token_hash` to verify here, or (links
  // sent before the email templates changed) the recovery session in the
  // fragment, which supabase-js picks up, or `error_code` (e.g. `otp_expired`)
  // when Supabase already found the link expired or used.
  const [link] = useState(() => authEmailLinkFor(pathname))
  const [state, setState] = useState<ResetPasswordState>(() => {
    if (link.kind === "error") return "linkExpired"
    if (link.kind === "tokenHash") return "verifying"
    return "form"
  })

  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    if (link.kind !== "tokenHash") return

    let isCurrent = true
    // NOTE: Right away, not on submit: the token works once, and verifying it
    // makes it useless to anything else that saw the link.
    verifyAuthEmailLinkOnce(
      (params) => supabase.auth.verifyOtp(params),
      link.tokenHash,
      link.type ?? "recovery",
    )
      .then(({ error }) => {
        if (!isCurrent) return
        if (error) console.warn("Verifying the reset link failed", error.code)
        setState(error ? "linkExpired" : "form")
      })
      .catch((error: unknown) => {
        console.error(error)
        if (isCurrent) setState("linkExpired")
      })

    return () => {
      isCurrent = false
    }
  }, [link])

  useEffect(() => {
    if (state !== "passwordUpdated") return

    const timeout = setTimeout(() => {
      navigate(SIGN_IN_PATH)
    }, REDIRECT_DELAY_MS)

    return () => clearTimeout(timeout)
  }, [navigate, state])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setIsSubmitting(true)

    const { error } = await updatePassword({ password })

    setIsSubmitting(false)

    if (error) {
      if (
        error.message ===
        "New password should be different from the old password."
      ) {
        setErrorMessage(
          t("pages.account.resetPassword.newPasswordShouldBeDifferent"),
        )
      } else if (error.name === "AuthSessionMissingError") {
        // NOTE: There is no recovery session, so the link was invalid,
        // expired or already used.
        setState("linkExpired")
      } else {
        throw error
      }
    } else {
      setState("passwordUpdated")
    }
  }

  if (state === "verifying") {
    return (
      <FunnelLayout header={<FunnelHeader />}>
        <div
          aria-busy="true"
          className="flex grow flex-col items-center justify-center"
        >
          <Spinner />
        </div>
      </FunnelLayout>
    )
  }

  if (state === "linkExpired") {
    return (
      <FunnelLayout header={<FunnelHeader />}>
        <div className="flex grow animate-fade-up flex-col gap-6 pb-8 pt-6">
          <StatusIcon icon={ClockIcon} tone="yellow" />

          <div className="space-y-3">
            <h1 className="vm-heading">
              {t("pages.account.resetPassword.linkExpired.heading")}
            </h1>

            <p className="vm-subheading">
              {t("pages.account.resetPassword.linkExpired.text")}
            </p>
          </div>

          <StickyFooter>
            <div className="space-y-4">
              <Button
                onClick={() => navigate("/account/forgot-password")}
                title={t(
                  "pages.account.resetPassword.linkExpired.requestNewLink",
                )}
              />

              <p className="text-center text-sm">
                <Link
                  className="font-semibold text-vm-muted underline underline-offset-4 hover:text-vm-ink"
                  to={SIGN_IN_PATH}
                >
                  {t("pages.account.forgotPassword.backToSignIn")}
                </Link>
              </p>
            </div>
          </StickyFooter>
        </div>
      </FunnelLayout>
    )
  }

  if (state === "passwordUpdated") {
    return (
      <FunnelLayout header={<FunnelHeader />}>
        <div
          aria-live="polite"
          className="flex grow animate-fade-up flex-col gap-6 pb-8 pt-6"
        >
          <StatusIcon icon={CheckIcon} tone="green" />

          <div className="space-y-3">
            <h1 className="vm-heading">
              {t("pages.account.resetPassword.dialog.title")}
            </h1>

            <p className="vm-subheading">
              <Trans
                components={{
                  signInLink: (
                    <Link
                      className="font-semibold text-vm-primary-text underline underline-offset-4"
                      to={SIGN_IN_PATH}
                    />
                  ),
                }}
                i18nKey="pages.account.resetPassword.dialog.text"
              />
            </p>
          </div>

          <div className="mx-auto h-1 w-24 overflow-hidden rounded-full bg-vm-line">
            <div
              className="h-full origin-left animate-grow-x rounded-full bg-vm-green"
              style={{
                animationDuration: `${REDIRECT_DELAY_MS}ms`,
                animationTimingFunction: "linear",
              }}
            />
          </div>
        </div>
      </FunnelLayout>
    )
  }

  return (
    <FunnelLayout header={<FunnelHeader />}>
      <form
        className="flex grow animate-slide-in-right flex-col gap-6 pt-4"
        onSubmit={(event) => void handleSubmit(event)}
      >
        <div className="animate-fade-up space-y-3">
          <h1 className="vm-heading">
            {t("pages.account.resetPassword.heading")}
          </h1>

          <p className="vm-subheading">
            {t("pages.account.resetPassword.enterNewPassword")}
          </p>
        </div>

        <div className="animate-fade-up space-y-3 [animation-delay:120ms]">
          <PasswordInput
            autoComplete="new-password"
            autoFocus
            disabled={isSubmitting}
            invalid={Boolean(errorMessage)}
            onChange={(event) => {
              setPassword(event.target.value)
              setErrorMessage(null)
            }}
            value={password}
          />

          {errorMessage ? (
            <p
              className="animate-fade-in text-sm font-medium text-vm-red"
              role="alert"
            >
              {errorMessage}
            </p>
          ) : (
            <p className="text-sm text-vm-muted">
              {t("pages.signUp.password.subheading")}
            </p>
          )}
        </div>

        <StickyFooter>
          <Button
            disabled={!validatePassword(password)}
            submitting={isSubmitting}
            title={t("pages.account.forgotPassword.resetPassword")}
            type="submit"
          />
        </StickyFooter>
      </form>
    </FunnelLayout>
  )
}
